import { useMemo, useState, useEffect, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { UploadCloud, CheckCircle2 } from 'lucide-react'
import { api, PublicDropInfo } from '@/services/api'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/Logo'

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="mb-6 flex items-center justify-center gap-2">
          <LogoMark size={24} />
          <span className="font-semibold tracking-tight">Freehold</span>
        </div>
        {children}
      </div>
    </div>
  )
}

export function PublicDropPage() {
  const { token } = useParams<{ token: string }>()
  const [info, setInfo] = useState<PublicDropInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadedCount, setUploadedCount] = useState<number | null>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!token) {
        setError('Invalid drop link')
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const data = await api.publicDrop.info(token)
        if (!mounted) return
        setInfo(data)
        setError(null)
      } catch (e) {
        if (!mounted) return
        setError((e as Error)?.message || 'Drop link unavailable')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [token])

  const canUpload = useMemo(() => !!token && files.length > 0 && !uploading, [token, files.length, uploading])

  const handleUpload = async () => {
    if (!token || files.length === 0) return
    try {
      setUploading(true)
      const result = await api.publicDrop.upload(token, files)
      setUploadedCount(result.count)
      setFiles([])
    } catch (e) {
      console.error('[PublicDropPage] Upload failed:', e)
      setError((e as Error)?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary" aria-label="Loading" />
          <p className="text-sm text-muted-foreground">Loading drop point…</p>
        </div>
      </Shell>
    )
  }

  if (error || !info) {
    return (
      <Shell>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <h1 className="mb-1 text-lg font-semibold">Drop point unavailable</h1>
          <p className="text-sm text-muted-foreground">{error || 'This drop link is no longer available.'}</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="flex flex-col items-center gap-4 p-7 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <UploadCloud className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">{info.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload files here — they're delivered immediately and can't be viewed back.
            </p>
          </div>

          <label
            className="flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-lg border border-dashed border-input bg-muted/30 px-4 py-7 text-center transition-colors hover:border-primary/50 hover:bg-muted/50"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              setFiles(Array.from(e.dataTransfer.files || []))
            }}
          >
            <UploadCloud className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm font-medium">Choose files or drag them here</span>
            <span className="text-xs text-muted-foreground">
              {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'} ready to upload` : 'Any file type'}
            </span>
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="hidden"
            />
          </label>

          <Button onClick={handleUpload} disabled={!canUpload} className="w-full">
            {uploading ? 'Uploading…' : 'Drop files now'}
          </Button>

          {uploadedCount != null && (
            <div className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" />
              Uploaded {uploadedCount} file{uploadedCount === 1 ? '' : 's'} successfully.
            </div>
          )}
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">Powered by Freehold</p>
    </Shell>
  )
}
